import { useState, useEffect } from "react";
import { Button, TextField, CircularProgress } from "@mui/material";
import { FaCloudUploadAlt, FaUniversity, FaQrcode } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  submitPayment,
  SubmitPaymentState,
  resetSubmitPayment,
  selectSubmitPayment,
} from "../slices/submit-payment.slice";
import { SupplierPaymentDetails } from "features/stock-ordering/core/domain/payment.model";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";

interface PaymentProcessPayProps {
  orderId: string | undefined;
  supplierPaymentInfo: SupplierPaymentDetails | null | undefined;
  onClose: () => void;
}

export function PaymentProcessPay(props: PaymentProcessPayProps) {
  const { orderId, supplierPaymentInfo, onClose } = props;

  const dispatch = useAppDispatch();
  const submitPaymentState = useAppSelector(selectSubmitPayment);

  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [remarks, setRemarks] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Handle successful submission
  useEffect(() => {
    if (submitPaymentState.status === SubmitPaymentState.success) {
      dispatch(resetSubmitPayment());
      onClose();
    }
  }, [submitPaymentState.status, dispatch, onClose]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, GIF, and PDF files are allowed");
        return;
      }

      setPaymentProof(file);

      // Create preview URL for images
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleSubmit = () => {
    if (!orderId || !paymentProof) {
      return;
    }

    dispatch(
      submitPayment({
        orderId: Number(orderId),
        paymentProof: paymentProof,
        remarks: remarks || undefined,
      }),
    );
  };

  const isSubmitting =
    submitPaymentState.status === SubmitPaymentState.inProgress;

  return (
    <div className="space-y-6">
      {/* Section Title */}
      <div className="flex items-center space-x-2 text-lg font-semibold text-gray-800">
        <FaUniversity className="text-green-600" />
        <span>Payment Information</span>
      </div>

      {/* Supplier Payment Details */}
      {supplierPaymentInfo ? (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <h3 className="text-md font-semibold text-green-800 mb-4">
            Supplier Bank Details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bank Information */}
            <div className="space-y-3">
              {supplierPaymentInfo.bank_name && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">
                    Bank Name
                  </p>
                  <p className="text-gray-900 font-semibold">
                    {supplierPaymentInfo.bank_name}
                  </p>
                </div>
              )}

              {supplierPaymentInfo.account_name && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">
                    Account Name
                  </p>
                  <p className="text-gray-900 font-semibold">
                    {supplierPaymentInfo.account_name}
                  </p>
                </div>
              )}

              {supplierPaymentInfo.account_number && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">
                    Account Number
                  </p>
                  <p className="text-gray-900 font-semibold font-mono text-lg">
                    {supplierPaymentInfo.account_number}
                  </p>
                </div>
              )}

              {supplierPaymentInfo.payment_instructions && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">
                    Instructions
                  </p>
                  <p className="text-gray-700 text-sm">
                    {supplierPaymentInfo.payment_instructions}
                  </p>
                </div>
              )}
            </div>

            {/* QR Code */}
            {supplierPaymentInfo.qr_code_image && (
              <div className="flex flex-col items-center justify-center">
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <img
                    src={`${REACT_APP_DOMAIN_URL}assets/uploads/qr-codes/${supplierPaymentInfo.qr_code_image}`}
                    alt="Payment QR Code"
                    className="max-w-[200px] max-h-[200px] object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent) {
                        const fallback = document.createElement("div");
                        fallback.className = "flex flex-col items-center";
                        fallback.innerHTML =
                          '<span class="text-gray-400 text-4xl mb-2">📱</span><span class="text-gray-500 text-sm">QR Code unavailable</span>';
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Scan to pay via GCash/Maya
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
          <p className="text-yellow-800 text-sm">
            Supplier payment details are not available. Please contact the
            supplier directly for payment instructions.
          </p>
        </div>
      )}

      {/* Upload Payment Proof */}
      <div className="space-y-3">
        <h3 className="text-md font-semibold text-gray-800 flex items-center space-x-2">
          <FaCloudUploadAlt className="text-green-600" />
          <span>Upload Payment Proof</span>
          <span className="text-red-500">*</span>
        </h3>

        <div
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
            paymentProof
              ? "border-green-400 bg-green-50"
              : "border-gray-300 hover:border-green-500"
          }`}
        >
          <input
            type="file"
            id="paymentProof"
            accept="image/*,.pdf"
            onChange={handleFileChange}
            className="hidden"
          />
          <label
            htmlFor="paymentProof"
            className="cursor-pointer flex flex-col items-center"
          >
            {previewUrl ? (
              <div className="space-y-3">
                <img
                  src={previewUrl}
                  alt="Payment proof preview"
                  className="max-w-[200px] max-h-[200px] object-contain rounded-lg shadow-md"
                />
                <p className="text-sm text-green-600 font-medium">
                  {paymentProof?.name}
                </p>
                <p className="text-xs text-gray-500">Click to change file</p>
              </div>
            ) : paymentProof ? (
              <div className="space-y-3">
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-3xl">📄</span>
                </div>
                <p className="text-sm text-green-600 font-medium">
                  {paymentProof.name}
                </p>
                <p className="text-xs text-gray-500">Click to change file</p>
              </div>
            ) : (
              <>
                <FaCloudUploadAlt className="text-4xl text-gray-400 mb-3" />
                <p className="text-gray-600 font-medium">
                  Drop payment screenshot here or click to upload
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Accepted: JPG, PNG, GIF, PDF (Max 5MB)
                </p>
              </>
            )}
          </label>
        </div>
      </div>

      {/* Remarks */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          Remarks (Optional)
        </label>
        <TextField
          multiline
          rows={3}
          fullWidth
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Add any notes about this payment (e.g., reference number, payment method used)..."
          variant="outlined"
          size="small"
        />
      </div>

      {/* Error Message */}
      {submitPaymentState.status === SubmitPaymentState.fail && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
          {submitPaymentState.message ||
            "Failed to submit payment. Please try again."}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            color: "gray",
            borderColor: "gray",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!paymentProof || isSubmitting}
          sx={{
            backgroundColor: "#16a34a",
            "&:hover": {
              backgroundColor: "#15803d",
            },
          }}
          startIcon={
            isSubmitting ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {isSubmitting ? "Submitting..." : "Submit Payment"}
        </Button>
      </div>
    </div>
  );
}
